import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Badge — small status pill.
 *
 * Tones map to the canonical token vocabulary. Coverage tones (covered /
 * progress / pending) are reserved for the federal map and the curricular
 * coverage UI; `accent` is reserved for jurisdictional or institutional
 * markers; `neutral` is the default.
 */
type Tone = 'neutral' | 'covered' | 'progress' | 'pending' | 'accent';

const tones: Record<Tone, string> = {
  neutral: 'border-line text-ink-muted bg-paper',
  covered: 'border-status-covered/40 text-status-covered bg-status-covered/10',
  progress: 'border-status-progress/40 text-status-progress bg-status-progress/10',
  pending: 'border-line text-ink-subtle bg-paper-tinted',
  accent: 'border-accent/40 text-accent bg-accent/5',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export type BadgeTone = Tone;
